import { generateSearchQuery } from "@app/utils/generateSearchQuery/generateSearchQuery";
import { Review } from "@core/domain/models/review.model";
import { ArticleMetadata } from "@core/domain/models/reviewItemMetadata/articleMetadata";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";
import { ReviewItemType } from "@core/domain/types/reviewItemType.type";
import axios from "axios";

export class OpenAlexApiClient {
  private readonly baseUrl = "https://api.openalex.org";

  async createReviewSearch(review: Review): Promise<{
    query?: string;
    filters?: string;
    date: string;
    reviewId: number;
    importSourceId: string;
    items: ArticleMetadata & { type: ReviewItemType.Article }[];
    queryTranslation?: string;
    url?: string;
  }> {
    const query = generateSearchQuery(BuiltInImportSourceId.OPEN_ALEX, review);
    const result = await this.search(query);
    return {
      query,
      date: new Date().toISOString(),
      reviewId: review.id,
      importSourceId: BuiltInImportSourceId.OPEN_ALEX,
      items: result.map((a) => ({
        ...a,
        type: ReviewItemType.Article,
      })),
      url: undefined,
    };
  }

  async searchWorks(query: string): Promise<ArticleMetadata[]> {
    const encodedQuery = encodeURIComponent(query);
    let page = 1;
    const perPage = 50;
    let allWorks: any[] = [];
    let hasMore = true;

    try {
      while (hasMore) {
        const response = await axios.get(
          `${this.baseUrl}/works?search=${encodedQuery}&page=${page}&per-page=${perPage}`,
        );
        allWorks = allWorks.concat(response.data.results);
        hasMore = response.data.meta.count > allWorks.length;
        page++;
      }
    } catch (error) {
      console.error("Error fetching works from OpenAlex API:", error);
      throw new Error("Failed to fetch works from OpenAlex API");
    }

    return allWorks.map((work: any) => this.mapToArticle(work));
  }

  async search(query: string): Promise<ArticleMetadata[]> {
    return this.searchWorks(query);
  }

  private mapToArticle(work: any): ArticleMetadata {
    return new ArticleMetadata({
      doi: this.getDoiFromUrl(work.doi),
      title: work.title,
      abstract: this.buildAbstractFromInvertedIndex(work.abstract_inverted_index),
      authors: work.authorships.map((au: any) => au.raw_author_name),
      url: work.doi,
      publishYear: work.publication_year.toString(),
      rawData: JSON.stringify(work),
      volumeNumber: work.biblio?.volume,
      issueNumber: work.biblio?.issue,
      //pagesNumber: work.biblio?.first_page + '-' + work.biblio?.last_page,
      //reference: work.referenced_works?.join(', '),
      pmid: work.id.pmid,
      potentialPdfUrl: work.primary_location.pdf_url,
      pmcId: work.id.pmcid,
    });
  }

  private getDoiFromUrl(url: string): string {
    const doiRegex = /(?:https?:\/\/)?(?:dx\.)?doi\.org\/([^\s]+)/;
    const match = doiRegex.exec(url);
    return match ? match[1] : "";
  }

  private buildAbstractFromInvertedIndex(invertedIndex: { [word: string]: number[] }) {
    if (!invertedIndex) return "";

    let maxPos = 0;
    for (const positions of Object.values(invertedIndex)) {
      maxPos = Math.max(maxPos, ...positions);
    }

    const textArray = Array(maxPos + 1).fill(null);

    for (const [word, positions] of Object.entries(invertedIndex)) {
      for (const pos of positions) {
        textArray[pos] = word;
      }
    }

    const text = textArray.map((word) => word || "").join(" ");

    return text;
  }
}
